const ACTIONS = {
    open: 'modal:open',
    close: 'modal:close',
};

const ATTR = {
    action: 'data-action',
    target: 'data-modal-target',
    id: 'data-modal-id',
    type: 'data-modal-type',
    disableLinking: 'data-disable-linking',
    isExclusive: 'data-is-exclusive',
    appendUrl: 'data-append-url',
};

const CLASS_NAMES = {
    visible: 'visible',
    transitioned: 'transitioned',
};

const LINK_PARAM = 'content';
const CLOSE_ALL_TARGET = '#all';
const FOCUSABLE_SELECTOR =
    "a[href], button:not([disabled]), details summary, input:not([disabled]):not([type='hidden']), select:not([disabled]), textarea:not([disabled]), iframe, object, [tabindex]:not([tabindex='-1'])";
const MODAL_BASE_Z_INDEX = 1000;

const modalRegistry = new Map();
const openModals = [];
let touchStartY = 0;
let shouldManageTouchScroll = false;

initCustomModalController();

class CustomModal extends HTMLElement {
    connectedCallback() {
        if (shouldPortalModal(this)) {
            this.dataset.modalPortaled = 'true';
            document.body.appendChild(this);
            return;
        }

        if (!this.hasAttribute('tabindex')) {
            this.setAttribute('tabindex', '-1');
        }

        if (!this.hasAttribute('role')) {
            this.setAttribute('role', 'dialog');
        }

        this.setAttribute('aria-hidden', this.isOpen ? 'false' : 'true');
        this.removeAttribute('open');

        const modalId = this.modalId;
        if (!modalId) {
            console.warn('[CustomModal] Missing data-modal-id');
            return;
        }

        if (modalRegistry.has(modalId) && modalRegistry.get(modalId) !== this) {
            console.warn(`[CustomModal] Duplicate data-modal-id "${modalId}" detected`);
            return;
        }

        modalRegistry.set(modalId, this);
    }

    disconnectedCallback() {
        const modalId = this.modalId;
        if (modalId && modalRegistry.get(modalId) === this) {
            modalRegistry.delete(modalId);
        }

        removeOpenModal(this);
        updateModalState();
    }

    get modalId() {
        return this.getAttribute(ATTR.id) || '';
    }

    get modalType() {
        return this.getAttribute(ATTR.type) || 'overlay';
    }

    get isOpen() {
        return this.classList.contains(CLASS_NAMES.visible);
    }

    get isExclusive() {
        return this.getAttribute(ATTR.isExclusive) === 'true';
    }

    get disableLinking() {
        return this.getAttribute(ATTR.disableLinking) === 'true';
    }

    get shouldAppendUrl() {
        return this.getAttribute(ATTR.appendUrl) === 'true';
    }

    get dialogElement() {
        const dialog = this.querySelector('[data-modal-content]');
        return dialog instanceof HTMLElement ? dialog : this;
    }

    openModal(options = {}) {
        const { triggerElement = null, updateUrl = this.shouldAppendUrl } = options;

        if (this.isExclusive) {
            closeAllModals({ exclude: this, restoreFocus: false, clearUrl: false });
        }

        this.openerElement = triggerElement instanceof HTMLElement ? triggerElement : this.openerElement || null;

        if (this.isOpen) {
            bringModalToFront(this);
            updateModalState();

            if (updateUrl) {
                updateModalUrl(this.modalId);
            }

            return;
        }

        this.classList.add(CLASS_NAMES.visible);
        this.setAttribute('aria-hidden', 'false');
        this.setAttribute('open', '');

        openModals.push(this);
        updateModalState();

        requestAnimationFrame(() => {
            if (!this.isOpen) return;

            this.classList.add(CLASS_NAMES.transitioned);
            focusModal(this);
        });

        if (updateUrl) {
            updateModalUrl(this.modalId);
        }
    }

    async closeModal(options = {}) {
        const { restoreFocus = true, clearUrl = this.shouldAppendUrl || readLinkedModalId() === this.modalId } = options;

        if (!this.isOpen) return;

        this.classList.remove(CLASS_NAMES.transitioned);
        this.removeAttribute('open');

        removeOpenModal(this);
        updateModalState();
        this.dispatchEvent(new CustomEvent('modal:closed', {
            bubbles: true,
            detail: {
                modalId: this.modalId,
            },
        }));

        await waitForModalTransition(this.dialogElement);

        this.classList.remove(CLASS_NAMES.visible);
        this.setAttribute('aria-hidden', 'true');

        if (clearUrl) {
            clearModalUrl(this.modalId);
        }

        if (restoreFocus && this.openerElement instanceof HTMLElement && this.openerElement.isConnected) {
            this.openerElement.focus();
        }
    }
}

if (!customElements.get('custom-modal')) {
    customElements.define('custom-modal', CustomModal);
}

function initCustomModalController() {
    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('keydown', handleDocumentKeyDown);
    document.addEventListener('focusin', handleDocumentFocusIn, true);
    document.addEventListener('touchstart', handleDocumentTouchStart, { capture: true, passive: true });
    document.addEventListener('touchmove', handleDocumentTouchMove, { capture: true, passive: false });
    document.addEventListener('touchend', handleDocumentTouchEnd, { capture: true, passive: true });
    document.addEventListener('touchcancel', handleDocumentTouchEnd, { capture: true, passive: true });

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', handleInitialLinkedModal, { once: true });
        return;
    }

    handleInitialLinkedModal();
}

function shouldPortalModal(modal) {
    if (modal.parentElement === document.body) return false;
    if (modal.dataset.modalPortaled === 'true') return false;
    if (modal.closest('[data-offer-page]')) return false;

    return true;
}

function handleInitialLinkedModal() {
    const linkedModalId = readLinkedModalId();
    if (!linkedModalId) return;

    const modal = getModalById(linkedModalId);
    if (!modal || modal.disableLinking) return;

    modal.openModal({
        restoreFocus: false,
        updateUrl: false,
    });
}

function handleDocumentClick(event) {
    if (!(event.target instanceof Element)) return;

    const openTrigger = event.target.closest(`[${ATTR.action}="${ACTIONS.open}"]`);
    if (openTrigger instanceof HTMLElement) {
        handleOpenClick(event, openTrigger);
        return;
    }

    const closeTrigger = event.target.closest(`[${ATTR.action}="${ACTIONS.close}"]`);
    if (closeTrigger instanceof HTMLElement) {
        handleCloseClick(event, closeTrigger);
        return;
    }

    if (!(event.target instanceof CustomModal)) return;
    if (!event.target.isOpen) return;

    event.target.closeModal({
        restoreFocus: false,
    });
}

function handleDocumentKeyDown(event) {
    const activeModal = getTopModal();
    if (!activeModal) return;

    if (event.key === 'Escape') {
        event.preventDefault();
        activeModal.closeModal();
        return;
    }

    if (event.key !== 'Tab') return;

    trapModalFocus(event, activeModal);
}

function handleDocumentFocusIn(event) {
    const activeModal = getTopModal();
    if (!activeModal) return;
    if (!(event.target instanceof Node)) return;
    if (activeModal.contains(event.target)) return;

    focusModal(activeModal);
}

function handleDocumentTouchStart(event) {
    if (!getTopModal()) return;
    if (!event.touches.length) return;

    touchStartY = event.touches[0].clientY;
    shouldManageTouchScroll = false;
}

function handleDocumentTouchMove(event) {
    const activeModal = getTopModal();
    if (!activeModal) return;
    if (!event.touches.length) return;

    if (!(event.target instanceof Node) || !activeModal.contains(event.target)) {
        event.preventDefault();
        return;
    }

    const touchY = event.touches[0].clientY;
    const scrollDelta = touchStartY - touchY;
    touchStartY = touchY;

    if (scrollDelta === 0) return;

    const scrollTarget = getScrollableModalElement(event.target, activeModal.dialogElement);

    if (shouldManageTouchScroll) {
        event.preventDefault();
        scrollElement(scrollTarget, scrollDelta);
        return;
    }

    if (!canScrollElement(scrollTarget, scrollDelta)) {
        shouldManageTouchScroll = true;
        event.preventDefault();
    }
}

function handleDocumentTouchEnd() {
    shouldManageTouchScroll = false;
    touchStartY = 0;
}

function handleOpenClick(event, trigger) {
    const modalTarget = normalizeModalTarget(trigger.getAttribute(ATTR.target) || '');
    if (!modalTarget || modalTarget === CLOSE_ALL_TARGET) {
        console.warn('[CustomModal] Missing valid data-modal-target on open trigger');
        return;
    }

    const modal = getModalById(modalTarget);
    if (!modal) {
        console.warn(`[CustomModal] Modal "${modalTarget}" not found`);
        return;
    }

    event.preventDefault();
    modal.openModal({
        triggerElement: trigger,
    });
}

function handleCloseClick(event, trigger) {
    const modalTarget = normalizeModalTarget(trigger.getAttribute(ATTR.target) || '');
    event.preventDefault();

    if (!modalTarget) {
        const parentModal = trigger.closest('custom-modal');
        if (!(parentModal instanceof CustomModal)) {
            console.warn('[CustomModal] Missing data-modal-target on close trigger');
            return;
        }

        parentModal.closeModal();
        return;
    }

    if (modalTarget === CLOSE_ALL_TARGET) {
        closeAllModals();
        return;
    }

    const modal = getModalById(modalTarget);
    if (!modal) {
        console.warn(`[CustomModal] Modal "${modalTarget}" not found`);
        return;
    }

    modal.closeModal();
}

async function closeAllModals(options = {}) {
    const { exclude = null, restoreFocus = true, clearUrl = true } = options;

    const modalsToClose = openModals.filter((modal) => modal !== exclude);
    const orderedModals = [...modalsToClose].reverse();

    await Promise.all(
        orderedModals.map((modal, index) =>
            modal.closeModal({
                restoreFocus: restoreFocus && index === 0,
                clearUrl,
            })
        )
    );
}

window.closeAllCustomModals = async function closeAllCustomModals(options = {}) {
    const { exclude = null, restoreFocus = true, clearUrl = true } = options;

    const modalsToClose = openModals.filter((modal) => modal !== exclude);
    const orderedModals = [...modalsToClose].reverse();

    await Promise.all(
        orderedModals.map((modal, index) =>
            modal.closeModal({
                restoreFocus: restoreFocus && index === 0,
                clearUrl,
            })
        )
    );
};

function getModalById(modalId) {
    return modalRegistry.get(modalId) || null;
}

function getTopModal() {
    return openModals[openModals.length - 1] || null;
}

function bringModalToFront(modal) {
    removeOpenModal(modal);
    openModals.push(modal);
}

function removeOpenModal(modal) {
    const modalIndex = openModals.indexOf(modal);
    if (modalIndex === -1) return;

    openModals.splice(modalIndex, 1);
}

function updateModalState() {
    const activeModal = getTopModal();

    for (const [index, modal] of openModals.entries()) {
        modal.style.setProperty('--custom-modal-layer', String(MODAL_BASE_Z_INDEX + index));
        modal.toggleAttribute('data-modal-active', modal === activeModal);

        if (modal === activeModal) {
            modal.removeAttribute('inert');
            modal.setAttribute('aria-modal', 'true');
            modal.setAttribute('aria-hidden', 'false');
            continue;
        }

        modal.setAttribute('inert', '');
        modal.setAttribute('aria-modal', 'false');
        modal.setAttribute('aria-hidden', 'true');
    }

    document.documentElement.toggleAttribute('scroll-lock', openModals.length > 0);
}

function trapModalFocus(event, modal) {
    const focusableElements = getFocusableElements(modal.dialogElement);
    if (!focusableElements.length) {
        event.preventDefault();
        modal.dialogElement.focus();
        return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;

    if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
    }

    if (event.shiftKey && (activeElement === firstElement || activeElement === modal.dialogElement)) {
        event.preventDefault();
        lastElement.focus();
    }
}

function focusModal(modal) {
    const initialFocusTarget = modal.querySelector('[data-modal-initial-focus]');
    if (initialFocusTarget instanceof HTMLElement) {
        initialFocusTarget.focus();
        return;
    }

    const focusableElements = getFocusableElements(modal.dialogElement);
    if (focusableElements.length) {
        focusableElements[0].focus();
        return;
    }

    modal.dialogElement.focus();
}

function getFocusableElements(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
        if (!(element instanceof HTMLElement)) return false;
        if (element.hasAttribute('inert')) return false;
        if (element.getAttribute('aria-hidden') === 'true') return false;
        if (element.getClientRects().length === 0) return false;

        return !element.hasAttribute('disabled');
    });
}

function getScrollableModalElement(target, modalDialog) {
    if (!(target instanceof Element)) return modalDialog;

    let element = target;

    while (element instanceof HTMLElement && modalDialog.contains(element)) {
        if (isScrollableElement(element)) {
            return element;
        }

        if (element === modalDialog) break;
        element = element.parentElement;
    }

    return modalDialog;
}

function isScrollableElement(element) {
    if (element.scrollHeight <= element.clientHeight + 1) return false;

    const { overflowY } = window.getComputedStyle(element);
    return overflowY === 'auto' || overflowY === 'scroll';
}

function canScrollElement(element, scrollDelta) {
    const canScrollDown = element.scrollTop + element.clientHeight < element.scrollHeight - 1;
    const canScrollUp = element.scrollTop > 0;

    return scrollDelta > 0 ? canScrollDown : canScrollUp;
}

function scrollElement(element, scrollDelta) {
    if (!canScrollElement(element, scrollDelta)) return;

    element.scrollTop += scrollDelta;
}

function normalizeModalTarget(target) {
    if (!target) return '';
    if (target === CLOSE_ALL_TARGET) return target;

    return target.replace(/^#/, '');
}

function readLinkedModalId() {
    const url = new URL(window.location.href);
    return normalizeModalTarget(url.searchParams.get(LINK_PARAM) || '');
}

function updateModalUrl(modalId) {
    const url = new URL(window.location.href);
    url.searchParams.set(LINK_PARAM, modalId);
    window.history.replaceState({}, '', url.toString());
}

function clearModalUrl(modalId) {
    const url = new URL(window.location.href);
    const linkedModalId = normalizeModalTarget(url.searchParams.get(LINK_PARAM) || '');
    if (linkedModalId && linkedModalId !== modalId) return;

    url.searchParams.delete(LINK_PARAM);
    window.history.replaceState({}, '', url.toString());
}

function waitForModalTransition(element) {
    const transitionDuration = getTransitionDurationMs(element);
    if (!transitionDuration) {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        let isResolved = false;

        const handleTransitionEnd = (event) => {
            if (event.target !== element) return;

            if (isResolved) return;
            isResolved = true;
            element.removeEventListener('transitionend', handleTransitionEnd);
            window.clearTimeout(timeoutId);
            resolve();
        };

        const timeoutId = window.setTimeout(() => {
            if (isResolved) return;
            isResolved = true;
            element.removeEventListener('transitionend', handleTransitionEnd);
            resolve();
        }, transitionDuration + 50);

        element.addEventListener('transitionend', handleTransitionEnd);
    });
}

function getTransitionDurationMs(element) {
    const computedStyle = window.getComputedStyle(element);
    const durations = computedStyle.transitionDuration.split(',').map(parseDurationValue);
    const delays = computedStyle.transitionDelay.split(',').map(parseDurationValue);

    return durations.reduce((longestDuration, duration, index) => {
        const delay = delays[index] || delays[0] || 0;
        return Math.max(longestDuration, duration + delay);
    }, 0);
}

function parseDurationValue(value) {
    const trimmedValue = value.trim();
    if (!trimmedValue) return 0;

    if (trimmedValue.endsWith('ms')) {
        return Number(trimmedValue.replace('ms', '')) || 0;
    }

    if (trimmedValue.endsWith('s')) {
        return (Number(trimmedValue.replace('s', '')) || 0) * 1000;
    }

    return Number(trimmedValue) || 0;
}
